/**
 * The signed-in account, backed by the server (D1) rather than localStorage.
 * A session token is persisted locally and sent as a bearer header; the account
 * itself (rating, record) is authoritative on the server. The `account` signal
 * is consumed across the UI (lobby, leaderboard, deck tracker).
 */
import { createSignal } from "solid-js";

export interface Account {
  readonly id: string;
  readonly username: string;
  readonly rating: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

const TOKEN_KEY = "cd-token";

const [account, setAccount] = createSignal<Account | null>(null);
export { account };

export function authToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function postAuth(
  path: string,
  username: string,
  password: string,
): Promise<{ error?: string }> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    return { error: "Network error — try again." };
  }
  const data = (await res.json().catch(() => ({}))) as {
    account?: Account;
    token?: string;
    error?: string;
  };
  if (!res.ok || !data.token || !data.account) {
    return { error: data.error ?? "Something went wrong." };
  }
  setToken(data.token);
  setAccount(data.account);
  return {};
}

export function register(username: string, password: string): Promise<{ error?: string }> {
  return postAuth("/api/auth/register", username, password);
}

export function login(username: string, password: string): Promise<{ error?: string }> {
  return postAuth("/api/auth/login", username, password);
}

export function signOut(): void {
  const token = authToken();
  setToken(null);
  setAccount(null);
  if (token) {
    void fetch("/api/auth/logout", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }
}

/** Re-fetch the account (e.g. after a ranked game changes the rating). */
export async function refreshAccount(): Promise<void> {
  const token = authToken();
  if (!token) return;
  try {
    const res = await fetch("/api/auth/me", { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const data = (await res.json()) as { account: Account };
      setAccount(data.account);
    } else if (res.status === 401) {
      setToken(null);
      setAccount(null);
    }
  } catch {
    /* offline — keep whatever we have */
  }
}

// Populate from a stored session on load.
void refreshAccount();
